(<template>
  <div class="post-form__section subject">
    <div v-if="showCategoriesSelectButton"
         :class="{'post-form__field--half-width': showSubjectField}"
         class="post-form__field">
      <p class="select-category-description"
         :class="{'error-color': inputFieldValidation}">{{ categoryText }}</p>
      <button class="subject__select select-category-btn"
              :class="{'category-input__error':inputFieldValidation}"
              @focus="openCategoryHelper"
              @click="openSelectCategoryModal">
        {{ buttonCategoryText }}
      </button>
      <p v-if="inputFieldValidation"
         class="text-error is-with-error">{{ categoryErrors[0] }}</p>
    </div>
    <sk-select v-if="showCategoriesSelectDropdown"
               ref="category"
               :description="categoryText"
               :default-value="selectCategoryText"
               :items-list="categoriesList"
               :ignore-categories="true"
               :preselected-value="categoryId"
               :validation-name="categoryErrorName"
               :class="{'post-form__field--half-width': !onSeparateLines}"
               class="post-form__field subject__select"
               @focus="openCategoryHelper"
               @csvaluechanged="setCategory" />
    <sk-input v-if="showSubjectField"
              ref="subject"
              :description="subjectText"
              :placeholder="subjectPlaceholder"
              :preselected-value="subject"
              :validation-name="subjectErrorName"
              :class="{'post-form__field--half-width': !onSeparateLines}"
              :reg-exp="/<\/?[^>]+(>|$)/g"
              class="post-form__field subject__select"
              @fieldvaluechanged="setSubject"
              @focus="openSubjectHelper" />
    <div v-if="showVatFreeBlock"
         class="vat-decision-reference">
      <sk-note v-if="showVatFreeBanner"
               :text="vatDecisionReferenceText" />
      <sk-input v-if="isVatDecisionReference"
                :description="vatDecisionReferenceDescription"
                :placeholder="vatDecisionReferenceDescription"
                :preselected-value="vatDecisionReference"
                :reg-exp="/[^0-9a-zA-ZÆØÅæøå\/:\-\\ ]/g"
                class="vat-decision-reference__input"
                @fieldvaluechanged="setVatDecisionReference" />
      <sk-checkbox v-if="isVatDecisionReference"
                   :preselected-value="acceptedVatFreePolicy"
                   :item-text="acceptedVatFreeText"
                   :validation-name="vatFreePolicyErrorName"
                   :class="{'is-with-error':vatFreePolicyErrors.length > 0}"
                   class="vat-decision-reference__checkbox"
                   @checkboxvaluechanged="setAcceptedVatFreePolicy" />
    </div>
  </div>
</template>)

<script>
  import {mapGetters, mapState, mapMutations, mapActions} from 'vuex';

  export default {
    props: {
      params: {
        type: Object,
        default() {
          return {};
        }
      }
    },
    computed: {
      ...mapGetters('OneAssignmentStore', ['isVatOverride']),
      ...mapGetters('PostingNotifierStore', ['showCategoriesSelect']),
      ...mapGetters('PostingStore/SubjectStore', ['getCategoryData']),
      ...mapGetters('PostingStore/PaymentMethodStore', ['vatRateOverride']),
      ...mapState('PostingStore/SubjectStore', {
        categories: (state) => state.categoriesList || [],
        translationCategories: (state) => state.translationCategoriesList || [],
        subject: (state) => state.subject,
        vatDecisionReference: (state) => state.vatDecisionReference,
        categoryId: (state) => state.category.toString(),
        acceptedVatFreePolicy: (state) => state.acceptedVatFreePolicy,
        // validation names
        subjectErrorName: (state) => state.errors.subject.name,
        categoryErrors: (state) => state.errors.category.errors,
        categoryErrorName: (state) => state.errors.category.name,
        vatDecisionReferenceErrorName: (state) => state.errors.vatDecisionReference.name,
        vatFreePolicyErrorName: (state) => state.errors.vatFreePolicy.name,
        vatFreePolicyErrors: (state) => state.errors.vatFreePolicy.errors,
      }),
      showVatFreeBlock() { return !this.isTranslationProject && this.getCategoryData.name && (this.isVatDecisionReference || this.showVatFreeBanner); },
      showCategoriesSelectButton() { return this.vatRateOverride && !this.isTranslationProject && this.showCategoriesSelect; },
      showCategoriesSelectDropdown() { return (this.isVatOverride && this.showDropdown && this.showCategoriesSelect) || this.isTranslationProject; },
      showVatRateOverride() { return (this.vatRateOverride && !this.isTranslationProject) || (this.isVatOverride && this.showDropdown); },
      categoryText() { return this.isTranslationProject ? this.$gettext('Subject') : this.$gettext('Category'); },
      acceptedVatFreeText() { return this.$gettext('I confirm that I have read and understood the information'); },
      vatDecisionReferenceDescription() { return this.$gettext('Decision reference number'); },
      vatDecisionReferenceText() {
        const template = this.$gettext('If a category containing the text "with a decision that includes interpreting" is selected, the assignment may be invoiced without value-added tax (VAT). An important condition for this is that the reference number of the decision is provided and that the decision can be presented to %{domain} for possible verification at a later date.');
        return this.$gettextInterpolate(template, {domain: this.$getString('domainData', 'platformName')});
      },
      showVatFreeBanner() { return this.getCategoryData?.vatFree; },
      isVatDecisionReference() { return this.getCategoryData?.vatDecisionReferenceNumberEnabled; },
      showSubjectField() {
        if (this.isTranslationProject) return false;
        if (!this.vatRateOverride && !this.isVatOverride) return true;
        return this.getCategoryData.subjectRequired;
      },
      inputFieldValidation() { return this.categoryErrors.length && !this.categoryId; },
      isOtherSubject() { return this.categoryId === 'other'; },
      subjectText() {
        if (this.isTranslationProject) {
          return this.$gettext('Enter subject');
        }

        return this.$gettext('Subject for interpreter');
      },
      subjectPlaceholder() { return this.$gettext('Write here...'); },
      buttonCategoryText() {
        return this.getCategoryData?.name
          ? this.getCategoryData?.name
          : this.$gettext('Select a category');
      },
      selectCategoryText() {
        return this.getCategoryData || !this.categoryId
          ? this.$gettext('Select a category')
          : this.$gettext('The selected category has been deleted');
      },
      computeCategorySelectionText() { return this.isTranslationProject ? this.$gettext('Select subject') : this.selectCategoryText; },
      currentLang() { return this.$route.params.lang; },
      paramsList() { return this.params || {}; },
      showDropdown() { return this.paramsList.showDropdown || false; },
      onSeparateLines() { return this.paramsList.onSeparateLines || false; },
      showAssignmentHelper() { return this.params.showAssignmentHelper; },
      isTranslationProject() { return this.params.isTranslationProject; },
      categoriesList() { return this.isTranslationProject ? this.translationCategories : this.categories; },
    },
    watch: {
      currentLang() {
        this.refetchCategories();
      }
    },
    methods: {
      ...mapActions('PostingNotifierStore', ['refetchCategories']),
      ...mapMutations('PostingStore/SubjectStore', [
        'setSubject',
        'setCategory',
        'setVatDecisionReference',
        'setAcceptedVatFreePolicy'
      ]),
      ...mapMutations('PostAssignmentHelperStore', [
        'openHelper',
        'setContext',
        'setPopoverReference',
      ]),
      closeModal() {
        this.$emit('closemodal');
      },
      openSelectCategoryModal() {
        this.closeModal();
        this.$store.commit('ModalStore/setModal', {
          component: 'select-category-modal',
          width: 800,
          data: {
            context: this,
            list: this.categoriesList
          }
        });
      },
      openSubjectHelper() {
        if (this.showAssignmentHelper) {
          this.setContext('interpretation');
          this.setPopoverReference(this.$refs.subject);
          this.openHelper('subject');
        }
      },
      openCategoryHelper() {
        if (this.showAssignmentHelper) {
          this.setContext('interpretation');
          this.setPopoverReference(this.$refs.category);
          if (!this.isTranslationProject) {
            this.openHelper('category');
          } else {
            this.openHelper('translationSubject');
          }
        }
      },
    }
  };
</script>

<style scoped>
.subject__select {
  flex-grow: 1;
}

.select-category-description {
  padding-bottom: 2px;
  font-size: 12px;
}

.select-category-btn {
  display: block;
  width: 100%;
  height: 35px;
  padding: 0 10px;
  border: 1px solid #dddfe7;
  border-radius: 4px;
  line-height: 33px;
  text-align: left;
}

.select-category-btn::after {
  content: var(--image-select-down-arrow-icon);
  display: flex;
  align-items: center;
  float: right;
  height: 100%;
  line-height: 1;
  cursor: pointer;
}

.select-category-btn:hover {
  box-shadow: inset 0 0 0 2px #646d8c;
}

.select-category-btn:focus {
  outline: var(--focus-outline);
}

.category-input__error {
  border: solid 1px #f04;
}

.vat-decision-reference {
  width: 100%;
  margin-bottom: 30px;
  padding: 8px;
  border-radius: 4px;
  background-color: var(--color-warning-200, #f0e8dd);
}

.vat-decision-reference__input {
  width: 60%;
  margin-bottom: 0;
  margin-left: 35px;
}

.vat-decision-reference__checkbox {
  margin-top: 16px;
  margin-left: 35px;
  font-size: 12px;
}
</style>

<style>
.vat-decision-reference__input .sk-input__input {
  background-color: #fff;
}
</style>
